import { useCallback, useRef, useMemo, useState } from "react"
import { isEqual, isNil, get, omit } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { Creators as GraphqlCreators } from "model/graphql"
import { OK, LOADING, DataStatus } from "helpers/data-statuses"
import { DEFAULT_HORIZON } from "helpers/forecasts/constants"
import { POSITIONS_SUBQUERY } from "hooks/rebalancer"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import {
  OptimizedPositionSet,
  OptimizationConfiguration,
  ApiOptimizationConfiguration,
  ApiOptimizationConstraints,
  OptimizationObjective,
  OptimizationOptions,
  ApiFactorRule,
} from "connected-components/optimize-experiments/hooks/types"
import {
  getDefaultConfiguration,
  type SetupSection,
  type RulesSection,
  type UniverseSection,
} from "connected-components/optimize-experiments/configuration-sections"
import {
  addMarketValueConstraints,
  applyConstraintDefaults,
  type MarketValues,
  transformFormToConfiguration,
  transformExposureConstraint,
  transformCompositionConstraints,
  type OptimizationSecuritiesInput,
} from "connected-components/optimize-experiments/components/helpers/configuration"
import { ForecastData } from "connected-components/optimize-experiments/hooks/types"

const OPTIMIZATION_QUERY = `#optimization
query ($modelId: String!, $on: Date!, $positionSet: PositionSetInput!, $base: [PositionSetInput!], $constraints: OptimizationConstraints!, $objectives: [OptimizationObjective]!, $options: OptimizationOptionsInput!, $forecast: ForecastInput, $securities: OptimizationSecuritiesInput) {
  model(id: $modelId) {
    optimization(
      positionSet: $positionSet
      base: $base
      on: [$on]
      objective:$objectives
      constraints:$constraints
      options:$options
      forecast:$forecast
      securities:$securities
    ) {
      positions {
        dates {
          ${POSITIONS_SUBQUERY}
        }
      }
    }
  }
}`

interface UseOptimizationOptions {
  overrideConfiguration?: OptimizationConfiguration | null
  setup?: SetupSection | null
  rules?: RulesSection | null
  universe?: UniverseSection | null
}

interface OptimizationResult {
  id: string
  status: DataStatus
  data?: {
    model?: {
      optimization?: {
        positions?: OptimizedPositionSet
      }
    }
  }
  apiMessages?: {
    errors?: {
      message: string
      code?: string
      path?: string[]
      locations?: { line: number, column: number }[]
    }[] | null
    messages?: unknown[] | null
  }
}

interface ForecastDataResult {
  id: string
  status: DataStatus
  data?: {
    model?: {
      simulation?: {
        forecast?: {
          forecastData?: ForecastData
        }
      }
    }
  }
}

interface ReduxState {
  global: {
    selected: {
      account?: string
    }
    data: {
      optimizationResult?: OptimizationResult
      forecastDataResult?: ForecastDataResult
    }
  }
}

interface UseOptimizationReturn {
  runOptimization: (force?: boolean) => void
  optimizationStatus: DataStatus | null
  optimizedPositionSet: OptimizedPositionSet | null
  getConfiguration: () => OptimizationConfiguration
  optimizationError: string | null
  getForecastData: () => void
  forecastData: ForecastData | null
  forecastDataStatus: DataStatus | null
}

function getForecastQuery(selectedCustomForecast: string) {
  const selectedForecast =
    selectedCustomForecast === "impliedReturns"
      ? "impliedReturns (riskFactors: MARKET)"
      : `custom(id: "${selectedCustomForecast}")`

  return `
  query ($modelId: String!, $portfolioId: String!, $on: Date!, $base: [PositionSetInput], $horizon: Int!) {
    model (id: $modelId) {
      simulation (positionSet: {
        id: $portfolioId,
        type: PORTFOLIO
      }, base: $base, from: $on, to: $on) {
        forecast(on: $on, horizon: $horizon) {
          forecastData: ${selectedForecast} {
            horizon
            total
            equities {
              id {
                ticker
                mic
                modelProviderId
              }
              expectedPercentReturn
            }
            swaps { id expectedPercentReturn }
          }
        }
      }
    }
  }`
}

export const useOptimization = (
  instrument: SelectedTypedInstrument | null,
  modelId: string,
  date: IsoDate,
  base: SelectedTypedInstrument | null,
  optimizationOptions: UseOptimizationOptions = {},
  portfolioMarketValues: MarketValues,
): UseOptimizationReturn => {
  const [previousOptimizationConstraints, setPreviousOptimizationConstraints] = useState<Partial<ApiOptimizationConstraints> | null>(null)
  const [previousObjectives, setPreviousObjectives] = useState<Partial<OptimizationObjective>[] | null>(null)
  const [previousOptions, setPreviousOptions] = useState<Partial<OptimizationOptions> | null>(null)
  const [previousForecast, setPreviousForecast] = useState<string | null>(null)
  const [previousSecuritySearch, setPreviousSecuritySearch] = useState<Partial<OptimizationSecuritiesInput> | null>(null)
  const { overrideConfiguration = null, setup = null, rules = null, universe = null } = optimizationOptions

  const dispatch = useDispatch()
  const account = useSelector((state: ReduxState) => state.global.selected.account)
  const currentOptimizationId = useRef<string | null>(null)
  const optimizationResult = useSelector((state: ReduxState): OptimizationResult | null => {
    if (!currentOptimizationId.current) return null
    const result = state.global.data.optimizationResult
    return result?.id === currentOptimizationId.current ? result : null
  })
  const currentForecastId = useRef<string | null>(null)
  const forecastDataResult = useSelector((state: ReduxState): ForecastDataResult | null => {
    if (!currentForecastId.current) return null
    const result = state.global.data.forecastDataResult
    return result?.id === currentForecastId.current ? result : null
  })

  const baseConfiguration: OptimizationConfiguration = useMemo(() => {
    const defaultConfiguration = getDefaultConfiguration()
    if (overrideConfiguration) {
      return overrideConfiguration
    }
    try {
      return transformFormToConfiguration(
        date,
        setup || defaultConfiguration.setup,
        rules || defaultConfiguration.rules,
        portfolioMarketValues,
        universe || defaultConfiguration.universe,
      )
    } catch {
      return transformFormToConfiguration(
        date,
        defaultConfiguration.setup,
        defaultConfiguration.rules,
        portfolioMarketValues,
        defaultConfiguration.universe,
      )
    }
  }, [date, setup, rules, universe, overrideConfiguration, portfolioMarketValues])

  const apiConfiguration: ApiOptimizationConfiguration = useMemo(() => {
    const constraintsWithDefaults = applyConstraintDefaults(baseConfiguration.constraints)
    const constraintsWithMarketValues = addMarketValueConstraints(
      constraintsWithDefaults,
      portfolioMarketValues,
    )

    const netMarketValue = portfolioMarketValues.longMarketValue - portfolioMarketValues.shortMarketValue

    const exposure: ApiFactorRule[] = constraintsWithMarketValues.exposure
      ? constraintsWithMarketValues.exposure
        .map((rule) => transformExposureConstraint(rule, base))
        .filter(item => item !== null)
      : []

    const composition: ApiFactorRule[] = constraintsWithMarketValues.composition
      ? constraintsWithMarketValues.composition
        .map((comp) => transformCompositionConstraints(comp, isNil(netMarketValue) ? null : netMarketValue, base))
        .filter(item => item !== null)
      : []

    return {
      ...baseConfiguration,
      constraints: {
        ...constraintsWithMarketValues,
        exposure,
        composition,
      },
    }
  }, [baseConfiguration, portfolioMarketValues, base])

  const { on, constraints, objectives, options, forecast, securities }: {
    on: IsoDate
    constraints: Partial<ApiOptimizationConstraints>
    objectives: Partial<OptimizationObjective>[]
    options: Partial<OptimizationOptions>
    securities: Partial<OptimizationSecuritiesInput>
    forecast: string | null
  } = apiConfiguration

  const optimizedPositionSet: OptimizedPositionSet | null =
    optimizationResult && optimizationResult.status === OK
      ? (get(optimizationResult, ["data", "model", "optimization", "positions"]) as OptimizedPositionSet | undefined ?? null)
      : null

  const optimizationStatus: DataStatus | null = optimizationResult?.status ?? null
  const optimizationError: string | null = optimizationResult?.apiMessages?.errors?.[0]?.message ?? null

  const forecastData: ForecastData | null =
    forecastDataResult && forecastDataResult.status === OK
      ? (get(forecastDataResult, ["data", "model", "simulation", "forecast", "forecastData"]) ?? null)
      : null

  const getForecastData = useCallback(() => {
    if (!instrument || !modelId || !date || !forecast) return null

    const timestamp = Date.now()
    const id = `forecastData-${modelId}-${date}-${forecast}-${JSON.stringify(instrument)}-${timestamp}`
    currentForecastId.current = id

    dispatch(GraphqlCreators.load(
      "forecastDataResult",
      id,
      getForecastQuery(forecast),
      {
        modelId,
        portfolioId: instrument.id,
        on: date,
        base,
        horizon: DEFAULT_HORIZON,
      },
      account,
    ))
  }, [instrument, modelId, date, forecast, base, dispatch, account])

  const haveAnyInputsChanged = useMemo(() => {
    return [
      [constraints, previousOptimizationConstraints],
      [objectives, previousObjectives],
      [options, previousOptions],
      [forecast, previousForecast],
      [securities, previousSecuritySearch],
    ].some(([current, previous]) => !isEqual(current, previous))
  }, [constraints, objectives, options, forecast, securities, previousOptimizationConstraints, previousObjectives, previousOptions, previousForecast, previousSecuritySearch])

  const runOptimization = useCallback((force = false) => {
    const shouldSkipOptimization = !force && !haveAnyInputsChanged
    if (!instrument || !modelId || !date || optimizationStatus === LOADING || shouldSkipOptimization) return

    const timestamp = Date.now()
    const id = `optimizationResult-${date}-${modelId}-${JSON.stringify(instrument)}-${timestamp}`
    currentOptimizationId.current = id

    setPreviousOptimizationConstraints(constraints)
    setPreviousObjectives(objectives)
    setPreviousOptions(options)
    setPreviousForecast(forecast)
    setPreviousSecuritySearch(securities)

    const instrumentIsPortfolio = instrument.type === "PORTFOLIO"
    const forecastInput = instrumentIsPortfolio && forecast && forecastData ? omit(forecastData, "total") : undefined
    dispatch(GraphqlCreators.load(
      "optimizationResult",
      id,
      OPTIMIZATION_QUERY,
      {
        modelId,
        on,
        positionSet: instrument,
        base,
        objectives,
        constraints,
        options,
        forecast: forecastInput,
        securities,
      },
      account,
    ))
  }, [instrument, modelId, date, base, optimizationStatus, dispatch, account, objectives, constraints, on, options, forecastData, forecast, securities, haveAnyInputsChanged])

  const getConfiguration = useCallback((): OptimizationConfiguration => {
    return {
      on: date,
      objectives,
      constraints: baseConfiguration.constraints,
      options,
      forecast,
      universe: baseConfiguration.universe,
      securities: baseConfiguration.securities,
    }
  }, [date, baseConfiguration.constraints, objectives, options, forecast, baseConfiguration.universe, baseConfiguration.securities])

  return {
    runOptimization,
    optimizationStatus,
    optimizedPositionSet,
    getConfiguration,
    optimizationError,
    getForecastData,
    forecastData,
    forecastDataStatus: forecastDataResult?.status ?? null,
  }
}
